import { Check, Copy, ExternalLink } from "lucide-react";
import { type ReactNode, useState } from "react";
import { signedInUrl } from "../lib/api";
import { Notice } from "./ui";

/** This page's address in the browser, without the ServiceM8 sign-in handover (#sm8=...). */
const pageUrl = () => location.href.split("#")[0] ?? location.href;

/**
 * For things that don't work inside ServiceM8 (e.g. downloads, which its frame blocks): says why,
 * with a link to the same page in a browser tab, and the address to copy. Tabs opened from inside
 * ServiceM8 can keep its limits, so pasting the address into a new tab is the sure way (the link
 * says so on hover; if copying is blocked too, the address shows, ready to copy by hand).
 */
export function OutsideServiceM8({ children }: Readonly<{ children: ReactNode }>) {
    const url = pageUrl();
    const [copied, setCopied] = useState<"yes" | "failed" | null>(null);

    async function copy() {
        try {
            await navigator.clipboard.writeText(url);
            setCopied("yes");
            setTimeout(() => setCopied(null), 2000);
        } catch {
            // The clipboard can be blocked inside ServiceM8 too: show the address to copy by hand.
            setCopied("failed");
        }
    }

    return (
        <Notice
            actions={
                <>
                    {/* Opened signed in (lib/api.ts signedInUrl); the copied link stays the plain address, to share. */}
                    <a
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => {
                            e.currentTarget.href = signedInUrl();
                        }}
                        className="btn-secondary px-2.5 py-1 text-xs"
                        title="If it doesn't open properly, copy the link into a new browser tab."
                    >
                        <ExternalLink className="size-3.5" /> Open in your browser
                    </a>
                    <button type="button" className="btn-secondary px-2.5 py-1 text-xs" onClick={() => void copy()}>
                        {copied === "yes" ? <Check className="size-3.5 text-brand-ink" /> : <Copy className="size-3.5" />}
                        {copied === "yes" ? "Copied" : "Copy link"}
                    </button>
                </>
            }
        >
            {children}
            {copied === "failed" ? (
                <input
                    className="input mt-2 text-xs"
                    readOnly
                    value={url}
                    aria-label="This page's address"
                    ref={(el) => el?.select()}
                    onFocus={(e) => e.target.select()}
                />
            ) : null}
        </Notice>
    );
}
